<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'templates.template',
    path: ':id',
    component: 'TemplateEditor',
    title: 'Templates',
    icon: 'fa-file-invoice',
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'TemplateEditor',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));

  const $data = computed(() => ({
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {},
    ux: {},
    dataRefs: {
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_cyfyhb91p_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };

  const act_7315092382398562_closeModal_cyfyhb91p =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_cyfyhb91p_getActionArgs,
    });
  const inst_evh_7315092382398562_close_h8v1pn0ck =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_cyfyhb91p,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_h8v1pn0ck],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_q4dx75yze =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_xjdbsyg7l =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_q4dx75yze,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_xjdbsyg7l],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_close_h8v1pn0ck,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_xjdbsyg7l,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_close_h8v1pn0ck = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_h8v1pn0ck.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_xjdbsyg7l = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_xjdbsyg7l.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="TemplateEditor"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div></div>
  </ItpPage>
</template>
